import type { History, Location } from 'history';
import type { ComponentType, ReactNode } from 'react';
import type { DropdownOption } from 'src/common/components/DropdownDS22';
import type { IconIds } from 'src/common/components/IconDS22';
import type { AppDispatch } from 'src/common/hooks/useAppDispatch';
import type { ImmutableTrip } from 'src/types/trips/Trips';

export type TripMenuComponentProps = {
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  activeClassName?: string;
  children?: ReactNode;
};

/**
 * Single source of truth for all trip action identifiers.
 * When adding a new action, add it here first, then update actions.ts accordingly.
 */
export const TRIP_ACTIONS = {
  CONTACT_SUPPORT: 'contact_support',
  SEND_MESSAGE: 'send_message',
  EDIT_OFFER: 'edit_offer',
  OPEN_IN_CALENDAR: 'open_in_calendar',
  CHARGE: 'charge',
  CANCEL_OFFER: 'cancel_offer',
  CANCEL_INQUIRY: 'cancel_inquiry',
  CANCEL_RESERVATION: 'cancel_reservation',
  DECLINE_INQUIRY: 'decline_inquiry',
  CANCEL_REQUESTED_CHANGES: 'cancel_requested_changes',
  ARCHIVE: 'archive',
  UNARCHIVE: 'unarchive',
} as const;

/** Union type of all valid action names in the trip menu */
export type TripActionName = (typeof TRIP_ACTIONS)[keyof typeof TRIP_ACTIONS];

export type TripAction = {
  call?: (tripId: number, payload?: Record<string, unknown>) => Promise<void> | void;
  cancelText?: string;
  component?: ComponentType<TripMenuComponentProps> & {
    displayName?: string;
    name?: string;
  };
  destination?: string;
  dropdownFieldName?: string;
  dropdownOptions?: DropdownOption[];
  dropdownPlaceholder?: string;
  icon: IconIds;
  isDisabled?: (trip: ImmutableTrip) => boolean;
  longDescription?: string;
  messageLabel?: string;
  name: TripActionName;
  onClick?: (
    dispatch: AppDispatch,
    trip: ImmutableTrip,
    options?: { history: History; location: Location }
  ) => void;
  placeholder?: string;
  shortDescription?: string;
  submitText?: string;
  title: string;
  url?: string;
  withDivider?: boolean;
  withMessageCharacterCounter?: boolean;
  withMessageField?: boolean;
};
